/* Copyright (c) 2017 Backpack Health LLC */
/* global document, window, Event */

import React from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';

import AppNavHealthEntryScrollable from '../../components/appNav/AppNavHealthEntryScrollable';

import RouteNames from '../RouteNames';
import HealthEntryCategoryNames from '../../../constants/HealthEntryCategoryNames';
import AppUtils from '../../../utilities/AppUtils';

import { useMedliSearch } from '../../hooks/useMedli';
import { useNavigate } from '../../hooks/useNavigate';
import { useHealthEntryForActiveProfile } from '../../hooks/useHealthEntry';
import { useHealthEntryTrackableListForActiveProfile } from '../../hooks/useTrackable';

interface Props {
  match: any;
  intl: any;
  allowEditTerms: boolean;
  isDefaultExpanded: boolean;
  healthEntryId: number;
  onDelete(): void;
  deletePhraseId: string;
}

const AppNavHealthEntryScrollableSmart: React.FC<Props> = (props) => {
  const { allowEditTerms, intl, match, isDefaultExpanded, onDelete, deletePhraseId } = props;

  const healthEntryId = Number(match.params.healthEntryId) || props.healthEntryId;

  const [, medliSearchActions] = useMedliSearch();
  const [, navActions] = useNavigate();
  const [{ category }, heActions] = useHealthEntryForActiveProfile(healthEntryId);
  const [{ enabledTrackables }] = useHealthEntryTrackableListForActiveProfile(healthEntryId);

  const [state, setState] = React.useState({
    showExpandedSection: isDefaultExpanded,
  });

  const healthEntry = heActions.instantiateHealthEntry();
  const healthEntryName = heActions.getName();
  const canonicalName = healthEntry.medliTerm.canonicalName();

  const titleLocalizationData = heActions.localizationData();
  let title = Boolean(titleLocalizationData)
    ? intl.formatMessage(titleLocalizationData, titleLocalizationData.values)
    : healthEntryName;

  const allowsSingleAnatomy = healthEntry.anatomy && !healthEntry.anatomy.allowsMany();
  const hasSingleAnatomy = healthEntry.anatomy && healthEntry.anatomy.hasAnatomy() && !healthEntry.anatomy.allowsMany();
  if (hasSingleAnatomy && (!state.showExpandedSection || !allowEditTerms)) {
    title = `${title} - ${healthEntry.anatomy.formattedNames()}`;
  }

  return (
    <AppNavHealthEntryScrollable
      onDelete={onDelete}
      deletePhraseId={deletePhraseId}
      allowEditTerms={allowEditTerms}
      canonicalName={canonicalName}
      hasSingleAnatomy={allowsSingleAnatomy}
      title={title}
      showMore={true}
      showExpandedSection={state.showExpandedSection}
      onToggleExpand={() => {
        setState({ showExpandedSection: !state.showExpandedSection });

        if (AppUtils.isIE11()) {
          const evt: any = document.createEvent('UIEvents');
          evt.initUIEvent('resize', true, false, window, 0);
          window.dispatchEvent(evt);
        } else {
          window.dispatchEvent(new Event('resize'));
        }
      }}
      editTitle={() => {
        medliSearchActions.setSearchParameters(category, healthEntryName);

        switch (category) {
          case HealthEntryCategoryNames.vaccination:
            navActions.goTo(RouteNames.healthEntryChangeList(healthEntryId));
            break;
          default:
            navActions.goTo(RouteNames.healthEntryChangeSearch(healthEntryId));
            break;
        }
      }}
    />
  );

};

export default withRouter(injectIntl(AppNavHealthEntryScrollableSmart));
